/**
 * buildSiteFiles — turn the live editor state (Yjs doc + grid store) into the
 * on-disk source file set that gets committed to a site's git repo.
 *
 * Shared by every commit path so they're byte-identical:
 *   - cloud publish (publishSite → clone + commit + push)
 *   - local commit  (LocalGitSession → OPFS-only commit, builds history)
 *   - local export  (zip the OPFS repo incl. .git)
 *
 * The file set is the rendered static site (one HTML doc per page + /duro.css),
 * the (lossy) render JSON (site.json + pages/*.json) and the exact editor
 * snapshot under `.editor/` (so a past version can be re-edited later). The
 * server's materialize step ships the rendered HTML verbatim and skips the JSON
 * source + editor snapshot (they're for re-editing, not serving).
 */
import type * as Y from "yjs";
import { useGridStore } from "@fredericrous/grid";
import { resolveGridLayouts } from "@website-builder/y-grid";
import {
  BUILDER_CAPABILITIES,
  PUBLISH_MARKER_PATH,
  type PublishMarker,
  type SiteThemeOrLegacy,
} from "@website-builder/schemas";

import {
  serializeSiteDoc,
  type EditorNode,
  type EditorEdge,
  type PublishError as SerializePublishError,
} from "./serializeSiteDoc.js";
import { serializeSnapshot, SNAPSHOT_PATH } from "./snapshotCodec.js";
import { readNavNested, readSiteTheme } from "../editor/siteSettings.js";
import { renderSiteClient } from "../theme/renderSiteClient.js";
import { serializeToHtml } from "@website-builder/lexical-html";

export interface BuildSiteFilesInput {
  readonly ydoc: Y.Doc;
  readonly siteId: string;
  readonly siteName: string;
  /** Override the doc-stored theme; normally absent so the site's own Brand wins. */
  readonly theme?: SiteThemeOrLegacy;
  /** Serve root for the rendered pages (default "/"); a preview's prefix. */
  readonly baseUrl?: string;
  /** An uploaded design system the pages render with: its widgets'
   *  sandbox-rendered HTML (by widget id), its stylesheet, and its manifest
   *  hash — stated in the publish marker for the server to check. */
  readonly customDesignSystem?: {
    readonly html: ReadonlyMap<string, string>;
    readonly css: string;
    readonly manifestSha: string;
  };
}

export interface BuiltSiteFiles {
  readonly files: ReadonlyArray<{ path: string; content: string }>;
  readonly warnings: ReadonlyArray<string>;
  /** Blocking problems (see serializeSiteDoc): publish refuses while any exist. */
  readonly errors: ReadonlyArray<SerializePublishError>;
}

export function gatherNodes(ydoc: Y.Doc): EditorNode[] {
  const out: EditorNode[] = [];
  for (const v of ydoc.getMap("nodes").values()) {
    const n = v as {
      id?: string;
      position?: { x?: number; y?: number };
      data?: { label?: string; slug?: string };
    };
    if (!n.id) continue;
    out.push({
      id: n.id,
      position: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
      label: n.data?.label ?? n.id,
      ...(n.data?.slug ? { slug: n.data.slug } : {}),
    });
  }
  return out;
}

export function gatherEdges(ydoc: Y.Doc): EditorEdge[] {
  const out: EditorEdge[] = [];
  for (const v of ydoc.getMap("edges").values()) {
    const e = v as {
      id?: string;
      source?: string;
      target?: string;
      label?: string;
      menuPosition?: number;
      hidden?: boolean;
    };
    if (!e.id || !e.source || !e.target) continue;
    out.push({
      id: e.id,
      source: e.source,
      target: e.target,
      ...(e.label !== undefined ? { label: e.label } : {}),
      ...(e.menuPosition !== undefined ? { menuPosition: e.menuPosition } : {}),
      ...(e.hidden ? { hidden: true } : {}),
    });
  }
  return out;
}

/** The site as its published document (what the pages render from). */
export function serializeForSite(input: BuildSiteFilesInput) {
  const grids = useGridStore.getState().grids;
  // The site's Brand theme lives in the doc; an explicit input.theme can override.
  const theme = input.theme ?? readSiteTheme(input.ydoc);
  return serializeSiteDoc({
    siteId: input.siteId,
    siteName: input.siteName,
    nodes: gatherNodes(input.ydoc),
    edges: gatherEdges(input.ydoc),
    grids,
    // Resolve each grid's responsive (desktop/mobile/hidden) layout straight
    // from the doc, so the published @media block is correct no matter which
    // breakpoint the canvas was left on.
    resolveLayouts: (gridId, columns) =>
      resolveGridLayouts(input.ydoc, gridId, columns),
    navNested: readNavNested(input.ydoc),
    ...(theme ? { theme } : {}),
  });
}

/** Build the full source file set (render JSON + editor snapshot). */
export function buildSiteFiles(input: BuildSiteFilesInput): BuiltSiteFiles {
  const { doc, files, warnings, errors } = serializeForSite(input);
  const grids = useGridStore.getState().grids;

  // Client-side render: the static site (one themed HTML doc per page + Duro's
  // stylesheet) as real Duro components — free mode has no server to materialize.
  // Additive alongside the render JSON; the JSON + .editor snapshot stay so a
  // site is still re-editable and a past version previewable.
  const rendered = renderSiteClient(doc, {
    serializeText: (s) =>
      serializeToHtml(s as Parameters<typeof serializeToHtml>[0]),
    ...(input.baseUrl ? { baseUrl: input.baseUrl } : {}),
    ...(input.customDesignSystem
      ? {
          customDesignSystem: {
            html: input.customDesignSystem.html,
            css: input.customDesignSystem.css,
          },
        }
      : {}),
  });

  const snapshotFile = {
    path: SNAPSHOT_PATH,
    content: serializeSnapshot({
      nodes: Array.from(input.ydoc.getMap("nodes").values()),
      edges: Array.from(input.ydoc.getMap("edges").values()),
      grids,
    }),
  };

  // Media is content-addressed and stored out-of-band (OPFS locally, the
  // object store remotely). The "download with history" export bundles it
  // under `_assets/<sha>` next to the working tree, so ignore it in git —
  // git versions the source + references, not the image bytes.
  const marker: PublishMarker = {
    builderCapabilities: BUILDER_CAPABILITIES,
    designSystem: input.customDesignSystem?.manifestSha ?? null,
  };
  const markerFile = {
    path: PUBLISH_MARKER_PATH,
    content: `${JSON.stringify(marker)}\n`,
  };
  const gitignoreFile = {
    path: ".gitignore",
    content:
      "# image bytes are content-addressed, synced out-of-band\n_assets/\n",
  };

  return {
    files: [
      ...files.map((f) => ({ path: f.path, content: f.content })),
      ...rendered.map((f) => ({ path: f.path, content: f.content })),
      snapshotFile,
      markerFile,
      gitignoreFile,
    ],
    warnings,
    errors,
  };
}
